import {
  ChakraProvider,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { signOutAction } from "../redux/slice/auth/authSlice";
import { useAppDispatch } from "../redux/hooks/useTypedSelector";
import { useUser } from "../hooks/use-user";
import theme from "../theme";
import "../styles/tailwind.css";
import { Icon } from "../components/public/ui";
import { AppSidebar } from "../components/app/AppSidebar";
import { APP_NAV_ITEMS, findNavTitle } from "../components/app/navItems";
import { isDateExpired } from "../utils/helperFuntions";

const settingsHref: Record<string, string> = {
  admin: "/admin/settings",
  student: "/student-settings",
  teacher: "/teacher/settings",
};

const AuthLayout = () => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!user) {
    window.location.href = "/";
  }

  const accountType = user?.accountType ?? "";
  const navItems = APP_NAV_ITEMS[accountType] ?? [];
  const title = findNavTitle(navItems, pathname);
  const initials =
    `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase() ||
    "?";
  const expired = !!user?.exp_date && isDateExpired(user.exp_date);

  return (
    <ChakraProvider theme={theme}>
      <div className="min-h-screen bg-surface font-body-md text-body-md text-on-surface antialiased">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block fixed inset-y-0 left-0 w-72 z-30 shadow-[1px_0_0_rgba(99,102,241,0.08)]">
          <AppSidebar items={navItems} expDate={user?.exp_date} />
        </aside>

        {/* Mobile / tablet drawer */}
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent maxW="18rem">
            <AppSidebar
              items={navItems}
              expDate={user?.exp_date}
              onNavigate={onClose}
              onClose={onClose}
            />
          </DrawerContent>
        </Drawer>

        <div className="lg:pl-72 flex flex-col min-h-screen">
          {/* Top bar */}
          <header className="sticky top-0 z-20 h-16 sm:h-20 bg-surface-container-lowest/90 backdrop-blur-md shadow-[0_1px_8px_rgba(0,0,0,0.04)]">
            <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center gap-3">
              <button
                type="button"
                onClick={onOpen}
                aria-label="Open menu"
                className="lg:hidden w-10 h-10 -ml-1 rounded-xl flex items-center justify-center text-on-surface hover:bg-surface-container-low"
              >
                <Icon name="menu" className="text-[24px]" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="font-headline-sm text-lg sm:text-headline-sm text-on-surface truncate">
                  {title || `Welcome, ${user?.firstName ?? ""}`}
                </h1>
              </div>
              <Menu placement="bottom-end">
                <MenuButton
                  className="rounded-full focus-visible:outline-none"
                  aria-label="Account menu"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="hidden sm:flex flex-col items-end leading-tight">
                      <span className="font-label-md text-label-md text-on-surface">
                        {user?.firstName} {user?.lastName}
                      </span>
                      <span className="font-body-sm text-xs text-on-surface-variant capitalize">
                        {accountType}
                      </span>
                    </span>
                    <span className="w-10 h-10 rounded-full bg-primary text-on-primary flex items-center justify-center font-label-md text-label-md">
                      {initials}
                    </span>
                    <Icon
                      name="expand_more"
                      className="hidden sm:block text-[20px] text-on-surface-variant"
                    />
                  </span>
                </MenuButton>
                <MenuList minW="15rem">
                  <div className="px-3 py-2">
                    <p className="font-label-md text-label-md text-on-surface truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="font-body-sm text-xs text-on-surface-variant truncate">
                      {user?.email}
                    </p>
                  </div>
                  <MenuDivider />
                  <MenuItem
                    as={Link}
                    to={settingsHref[accountType] ?? "/dashboard"}
                    icon={<Icon name="settings" className="text-[18px]" />}
                  >
                    Settings
                  </MenuItem>
                  <MenuItem
                    onClick={() => dispatch(signOutAction())}
                    icon={<Icon name="logout" className="text-[18px]" />}
                  >
                    Sign out
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>
          </header>

          {expired && (
            <Link
              to="/package-plan"
              className="mx-4 sm:mx-6 lg:mx-8 mt-4 sm:mt-6 flex items-start sm:items-center gap-3 p-4 rounded-2xl bg-accent-warm/10 text-on-surface hover:bg-accent-warm/15 transition-colors"
            >
              <Icon
                name="warning"
                className="text-accent-warm text-[22px] shrink-0"
                filled
              />
              <span className="flex-1 font-body-sm text-body-sm">
                Your subscription has expired. You can only practice using one
                free mock test. Please upgrade your plan to practice all the
                mock tests.
              </span>
              <span className="hidden sm:inline-flex font-label-md text-label-md text-primary whitespace-nowrap">
                Upgrade plan →
              </span>
            </Link>
          )}

          <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </ChakraProvider>
  );
};

export default AuthLayout;
